import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PointCaMensuel } from '@sirajoptic/shared';
import { dh, entier, pluriel } from '@/lib/format';

/* Les couleurs du thème, en littéraux : Recharts écrit dans des attributs SVG
 * et ne voit pas les classes Tailwind. */
const MOUTARDE = '#D9A404';
const MOUTARDE_PALE = '#EDCF6B';
const MOUTARDE_SOMBRE = '#B8860B';
const ENCRE = '#111111';
const TRAIT = '#E7E5E4';

/**
 * Chiffre d'affaires des douze derniers mois, calculé sur les **commandes** —
 * le livre de recettes réel. L'aire superpose l'encaissé, dont l'écart avec
 * les barres donne à voir les restes dus.
 *
 * Deux détails portent tout le sens de l'écran.
 *
 * **La dernière barre est plus claire.** Le mois en cours est incomplet : le
 * 10 du mois il ne pèse qu'un tiers de ce qu'il pèsera. Peint comme les
 * autres, il se lirait comme un effondrement. La teinte pâle dit « pas
 * terminé » sans qu'on ait à l'écrire.
 *
 * **Le trait pointillé est la moyenne des mois révolus** — le mois courant en
 * est exclu, sans quoi il tirerait la référence vers le bas et se comparerait
 * à lui-même.
 */
export function CaChart({ donnees }: { donnees: PointCaMensuel[] }) {
  const vide = donnees.every((d) => d.ca === 0);

  if (vide) {
    return (
      <p className="px-4 py-12 text-center text-sm text-ink-muted">
        Aucune commande sur les douze derniers mois.
      </p>
    );
  }

  const revolus = donnees.slice(0, -1);
  const moyenne = revolus.length
    ? revolus.reduce((somme, d) => somme + d.ca, 0) / revolus.length
    : 0;

  return (
    <>
      <div className="h-64 w-full px-1 py-3">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={donnees} margin={{ top: 12, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid vertical={false} stroke={TRAIT} />
            <XAxis
              dataKey="libelle"
              tickLine={false}
              axisLine={{ stroke: TRAIT }}
              tick={{ fill: '#78716C', fontSize: 11 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={64}
              tick={{ fill: '#78716C', fontSize: 11 }}
              tickFormatter={(v: number) => entier(v)}
            />
            <Tooltip cursor={{ fill: '#FBF3D9' }} content={<InfoBulle />} />

            {moyenne > 0 ? (
              <ReferenceLine
                y={moyenne}
                stroke={MOUTARDE_SOMBRE}
                strokeDasharray="4 4"
                strokeWidth={1}
              />
            ) : null}

            <Bar dataKey="ca" radius={[3, 3, 0, 0]} maxBarSize={38}>
              {donnees.map((d, index) => (
                <Cell
                  key={d.mois}
                  fill={index === donnees.length - 1 ? MOUTARDE_PALE : MOUTARDE}
                />
              ))}
            </Bar>

            <Area
              type="monotone"
              dataKey="encaisse"
              stroke={ENCRE}
              strokeWidth={1.5}
              fill={ENCRE}
              fillOpacity={0.06}
              dot={false}
              activeDot={{ r: 3, fill: ENCRE, stroke: '#FFFFFF', strokeWidth: 1.5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-4 py-2 text-xs text-ink-muted">
        <Legende classe="bg-mustard-500" libelle="Commandes et ventes" />
        <Legende classe="bg-ink" libelle="Encaissé" />
        <Legende classe="bg-mustard-300" libelle="Mois en cours, incomplet" />
        {moyenne > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-0 w-3 border-t border-dashed border-mustard-600" />
            Moyenne des mois révolus · {dh(moyenne)}
          </span>
        ) : null}
      </div>
    </>
  );
}

function Legende({ classe, libelle }: { classe: string; libelle: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className={`h-2 w-2 rounded-sm ${classe}`} />
      {libelle}
    </span>
  );
}

/**
 * Infobulle maison : celle de Recharts n'affiche qu'une série à la fois et ne
 * sait pas dire le reste dû, qui est justement ce que l'écart entre la barre
 * et la ligne raconte.
 */
function InfoBulle({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { payload: PointCaMensuel }[];
  label?: string;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  const reste = point.ca - point.encaisse;

  return (
    <div className="rounded-card border border-line bg-white px-3 py-2 shadow-pop">
      <p className="text-xs font-semibold text-ink">{label}</p>
      <dl className="mt-1.5 space-y-1">
        <LigneBulle classe="bg-mustard-500" terme="Chiffre d'affaires" valeur={dh(point.ca)} />
        <LigneBulle classe="bg-ink" terme="Encaissé" valeur={dh(point.encaisse)} />
        {/* Le reste peut être négatif : un client règle en janvier une commande
            de décembre. C'est exact, et l'afficher tel quel vaut mieux que de
            le masquer. */}
        <LigneBulle classe="bg-line" terme="Reste" valeur={dh(reste)} />
      </dl>
      <p className="mt-1.5 border-t border-line pt-1.5 text-xs text-ink-muted">
        {pluriel(point.commandes, 'opération')}
      </p>
    </div>
  );
}

function LigneBulle({
  classe,
  terme,
  valeur,
}: {
  classe: string;
  terme: string;
  valeur: string;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-sm ${classe}`} />
      <dt className="text-ink-muted">{terme}</dt>
      <dd className="ml-auto font-medium text-ink tabular">{valeur}</dd>
    </div>
  );
}
